import { useEffect } from "react";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import Story from "@/components/landing/Story";
import WhatILearned from "@/components/landing/WhatILearned";
import GentleApproach from "@/components/landing/GentleApproach";
import WhyIMadeThis from "@/components/landing/WhyIMadeThis";
import ScrollAnchor from "@/components/landing/ScrollAnchor";
import LeadMagnet from "@/components/landing/LeadMagnet";
import FinalMessage from "@/components/landing/FinalMessage";
import Footer from "@/components/landing/Footer";
import StickyMobileCTA from "@/components/landing/StickyMobileCTA";
import useSeo from "@/hooks/use-seo";

interface IndexProps {
  seo?: {
    path: string;
    title: string;
    description: string;
    /** Optional element id within the page to scroll to on mount */
    scrollTo?: string;
  };
}

const DEFAULT_SEO = {
  path: "/",
  title: "Eliza Reed Wellness — A gentle letter for women after 45",
  description:
    "A calm, honest letter from Eliza about body changes after 45, what gently helped, and small steps that don't ask too much of you. Free 7-day belly reset guide.",
};

const Index = ({ seo }: IndexProps = {}) => {
  const active = seo ?? DEFAULT_SEO;
  useSeo({
    path: active.path,
    title: active.title,
    description: active.description,
  });

  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>(".fade-in-up");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (seo?.scrollTo) {
      const el = document.getElementById(seo.scrollTo);
      if (el) {
        // Defer until layout settles
        requestAnimationFrame(() =>
          el.scrollIntoView({ behavior: "smooth", block: "start" }),
        );
      }
    }
  }, [seo?.scrollTo]);

  return (
    <main className="min-h-screen bg-background">
      <Header />
      <Hero />
      <Story />
      <WhatILearned />
      <GentleApproach />
      <WhyIMadeThis />
      <ScrollAnchor />
      <LeadMagnet />
      <FinalMessage />
      <Footer />
      <StickyMobileCTA />
    </main>
  );
};

export default Index;
